import React, { ChangeEvent, memo, useCallback, useMemo, useState } from 'react'
import tw from 'twin.macro'
import {
  UserNotificationsConfigEnableds,
  UserNotificationsConfigPeriods,
} from 'src/interfaces/user'
import { useDispatch, useSelector } from 'react-redux'
import { UpdateUserNotificationsConfig } from 'src/constants/actionTypes'
import { getUserNotificationConfig } from 'src/selectors/user'
import { IOSSwitch } from 'src/components/shared/IOSSwitch'
import { Typography } from '@mui/material'
import { colors } from 'src/theme'
import { BasicButtonGroup } from 'src/components/shared/form/BasicButtonGroup'
import { userNotificationPeriods } from './constants'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { UserNotificationPeriod } from 'src/constants/user'

interface Props {
  title: string
  enabledValueName: keyof UserNotificationsConfigEnableds
  periodValueName: keyof UserNotificationsConfigPeriods
  enabledPeriods?: UserNotificationPeriod[]
}

const EmailNotificationsConfigItemComponent = ({
  title,
  enabledValueName,
  periodValueName,
  enabledPeriods,
}: Props) => {
  const dispatch = useDispatch()

  const userNotificationConfig = useSelector(getUserNotificationConfig)

  const [enabled, setEnabled] = useState(!!userNotificationConfig?.[enabledValueName])
  const [period, setPeriod] = useState<string | undefined>(
    (userNotificationConfig?.[periodValueName] as string) || undefined,
  )

  const handleChangeEnabled = useCallback(
    async (event: ChangeEvent, checked: boolean) => {
      try {
        setEnabled(checked)

        await createAsyncAction(
          dispatch,
          UpdateUserNotificationsConfig.request({ [enabledValueName]: checked }),
        )
      } catch (error) {
        setEnabled(!checked)
      }
    },
    [enabledValueName],
  )

  const handleChangePeriod = useCallback(
    async (newPeriod: string) => {
      try {
        setPeriod(newPeriod)

        await createAsyncAction(
          dispatch,
          UpdateUserNotificationsConfig.request({ [periodValueName]: newPeriod }),
        )
      } catch (error) {
        setPeriod(period)
      }
    },
    [periodValueName, period],
  )

  const periodOptions = useMemo(() => {
    if (!enabledPeriods?.length) {
      return userNotificationPeriods
    }

    return userNotificationPeriods.filter((option) => enabledPeriods.includes(option.value))
  }, [enabledPeriods])

  return (
    <div css={tw`bg-white rounded-lg p-4`}>
      <div css={tw`flex flex-row items-center justify-between mb-4`}>
        <div>
          <Typography css={tw`mb-1`} variant="h6">
            {title}
          </Typography>
          <Typography
            variant="body2"
            color={enabled ? colors.COLORS_GREEN_500 : colors.SHADES_GREY_400}
          >
            {enabled ? 'Enabled' : 'Disabled'}
          </Typography>
        </div>
        <IOSSwitch
          checked={enabled}
          inputProps={{ 'aria-label': 'controlled' }}
          onChange={handleChangeEnabled}
        />
      </div>
      <BasicButtonGroup
        value={period}
        disabled={!enabled}
        options={periodOptions}
        onChange={handleChangePeriod}
      />
    </div>
  )
}

export const EmailNotificationsConfigItem = memo(EmailNotificationsConfigItemComponent)
